import React from 'react';
import type { PositionDetail } from '@/types/trades.types';
import { useTradeAccruals } from '@/hooks/trades/use-trade-accruals';
import { effectiveRates } from '@/services/trades/effective-rate';
import { RateWaterfall } from '../analytics/RateWaterfall';
import { PnlTable } from '../analytics/PnlTable';
import { AdequacyCard } from '../analytics/AdequacyCard';
import { AccrualChart } from '../analytics/AccrualChart';
import {
  accrualCurrency,
  hasSettlementDailyReturn,
  isTerminalStatus,
} from '../analytics/analytics-economics';

interface AnalyticsTabProps {
  trade: PositionDetail;
}

/**
 * Economics tab (Phase-15 redesign) — an equal `1fr 1fr` grid: the left column
 * is "how we earn" (rate build-up → daily P&L decomposition), the right column
 * is "how we're covered" (margin adequacy → accrual chart).
 *
 * Left column = "how we earn" (RateWaterfall + PnlTable); right column = "how
 * we're covered" (AdequacyCard + daily AccrualChart). Lifecycle timing was
 * dropped — the KPI strip's accrued sub counts accrual-days (distinct accrual
 * dates = chart bars, LR-414 B), days-to-maturity lives in Trade Terms, and both
 * the calendar days-active metric and the % term-elapsed bar were retired. Every
 * money figure renders through string-safe Decimal helpers; no P&L/ROI fiction (HON-04).
 */
export function AnalyticsTab({ trade }: AnalyticsTabProps) {
  const { data: accruals, isLoading, isError } = useTradeAccruals(trade.id);
  // Mirror KpiStrip's isTerminal — pass to AccrualChart so the dashed forward
  // projection bar drops on matured/closed/cancelled trades (audit N10; the
  // KPI hero is already masked to "—", a dashed projection of −$176/day on a
  // 4-months-matured position contradicts it). Single source:
  // analytics-economics.TERMINAL_STATUSES (LR-208 r5 M3).
  const isTerminal = isTerminalStatus(trade.status);
  // LR-749: the chart plots the booked rows and projects forward from their sum,
  // so the forward daily has to be in the rows' currency. Converted rows pair with
  // the settlement twin; without a usable twin the chart falls back to the latest
  // booked day, which is in that currency already. Rows in two currencies (a
  // statement issued before the switch kept its own) are not one series at all.
  const currency = accrualCurrency(trade, accruals);
  const { dailyEarnings, dailyEarningsConverted } = effectiveRates(trade);
  const forwardDailyNet =
    currency === trade.currency
      ? dailyEarnings
      : hasSettlementDailyReturn(trade)
        ? dailyEarningsConverted
        : null;

  return (
    <div className="mx-auto grid max-w-screen-2xl grid-cols-1 gap-4 xl:grid-cols-2">
      {/* Left — how we earn */}
      <div className="grid content-start gap-4">
        <RateWaterfall trade={trade} />
        <PnlTable trade={trade} />
      </div>

      {/* Right — how we're covered */}
      <div className="grid content-start gap-4">
        <AdequacyCard trade={trade} />
        <AccrualChart
          accruals={accruals}
          isLoading={isLoading}
          isError={isError}
          currency={currency ?? undefined}
          unchartableReason={
            currency === null
              ? `Accruals are booked in two currencies (${trade.currency} and ` +
                `${trade.settlement_currency}), so they cannot be charted as one series.`
              : undefined
          }
          forwardDailyNet={forwardDailyNet}
          isTerminal={isTerminal}
        />
      </div>
    </div>
  );
}
